import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { urls } from "../urls";

export enum BANNER_SEVERITY {
    info = "INFO",
    error = "ERROR",
    success = "SUCCESS",
    warning = "WARNING"
}

export interface MaintenanceBannerType {
    title: string;
    message: string;
    startDate: string;
    endDate: string;
    severity: BANNER_SEVERITY;
    isActive: boolean;
    isClosable: boolean;
}

interface MaintenanceBannerApiResponse {
    id: string;
    title: string;
    description: string;
    severity: string;
    start_date: string;
    end_date: string;
    is_closable: boolean;
    created_at?: string;
    last_update?: string;
    deleted_at?: string | null;
    created_by?: string;
    updated_by?: string;
}

const cleanDateString = (value?: string): string => {
    if (!value || value === "null") return "";

    return value
        .trim()
        .replace(/^"+/, "")
        .replace(/"+$/, "");
};

const mapBannerResponse = (
    data: MaintenanceBannerApiResponse | null
): MaintenanceBannerType | null => {
    if (!data) return null;

    const message = data.description?.trim();
    if (!message) return null;

    const severity = Object.values(BANNER_SEVERITY).includes(data.severity as BANNER_SEVERITY)
        ? (data.severity as BANNER_SEVERITY)
        : BANNER_SEVERITY.info;

    const cleanedStartDate = cleanDateString(data.start_date);
    const cleanedEndDate = cleanDateString(data.end_date);

    return {
        title: data.title?.trim() || "",
        message,
        startDate: cleanedStartDate
            ? dayjs(cleanedStartDate).format("MM/DD/YYYY hh:mm A")
            : "",
        endDate: cleanedEndDate
            ? dayjs(cleanedEndDate).format("MM/DD/YYYY hh:mm A")
            : "",
        severity,
        isActive: true,
        isClosable: Boolean(data.is_closable),
    };
};



export const useMaintenanceBanner = () => {
    const maintenanceBannerBaseUrl = urls.MAINTENANCE_BANNER;
    const maintenanceBannerUrl =
        `${process.env.NODE_ENV === "production" ? maintenanceBannerBaseUrl : ""}/maintenanceBanner/currentActive`;

    const [maintenanceBanner, setMaintenanceBanner] = useState<MaintenanceBannerType | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        const fetchMaintenanceBanner = async () => {
            try {
                const response = await fetch(`${maintenanceBannerUrl}`, {
                    method: "GET",
                    credentials: "include",
                });

                if (!response.ok) {
                    throw new Error(`Failed to fetch maintenance banner: ${response.status}`);
                }

                const data: MaintenanceBannerApiResponse = await response.json();
                const mappedBanner = mapBannerResponse(data);

                setMaintenanceBanner(mappedBanner);
            } catch (error) {
                console.error("Error fetching maintenance banner:", error);
                setMaintenanceBanner(null);
            } finally {
                setLoading(false);
            }
        };

        fetchMaintenanceBanner();
    }, []);

    return { maintenanceBanner, loading };
};
